import { zipSync } from "fflate";
import {
  bounceStemsOffline,
  sanitizeStemFilename,
  type BounceClipOptions,
  type BounceStemsResult,
} from "../../../../src/lib/audio";
import { downloadBlob, encodeWavPcm16 } from "./wav";

export type StemZipOptions = BounceClipOptions & {
  /** Zip archive filename (with .zip). */
  filename: string;
};

/**
 * Bounce occupied tracks as solo stems, pack PCM WAVs into a zip via fflate,
 * and trigger a browser download.
 */
export async function bounceStemsToZip(options: StemZipOptions): Promise<BounceStemsResult> {
  const { filename, ...bounceOpts } = options;
  const result = await bounceStemsOffline(bounceOpts);
  const entries: Record<string, Uint8Array> = {};
  const usedNames = new Set<string>();

  for (const stem of result.stems) {
    const index = String(stem.slot + 1).padStart(2, "0");
    let base = `${index}-${sanitizeStemFilename(stem.name)}`;
    if (usedNames.has(base)) {
      base = `${base}-s${stem.slot}`;
    }
    usedNames.add(base);
    const wav = encodeWavPcm16(stem.samples, stem.sampleRate, stem.channels ?? 2);
    entries[`${base}.wav`] = new Uint8Array(wav);
  }

  // Store (level 0): WAV is already uncompressed PCM — deflate buys little.
  const zipped = zipSync(entries, { level: 0 });
  downloadBlob(
    new Blob([zipped], { type: "application/zip" }),
    filename.endsWith(".zip") ? filename : `${filename}.zip`,
  );
  return result;
}
